/**
 * Running-campaign detail figures, sourced from the real backends.
 *
 * Resolves everything the detail shows from live data for ONE campaign:
 *  - the **Casino Ad Service** campaign performance (`getAdCampaignPerformance`,
 *    `breakdown=summary`) for the hero's total Ad Spend (lifetime `cost`), the Top 10
 *    Websites (`top_sites` impressions + clicks) and the Ad Spend / CPA KPI columns
 *    (`cost`, and `cost / FTDs`), queried over the lifetime + four comparison windows;
 *  - the **Owners Hub Stats** `daily_affiliate_activity` data set, aggregated on the
 *    `campaign` dimension and FILTERED to this campaign's id, for the conversion KPIs
 *    (Registrations, FTDs, FTD Value, Turnover) over the Today / This-month windows
 *    with vs-yesterday / vs-last-month deltas.
 *
 * Figures stay loading until BOTH sources resolve (clean reveal); a stats failure flips
 * that column to `N/A`, an ad failure flips Ad Spend / CPA + the hero to `N/A`. In the
 * test env the campaign group carries no tagged activity yet, so the conversion figures
 * read 0 (and the per-FTD ratios read `–`) and spend reads $0 until delivery — the
 * wiring lights up the moment that data lands. Mapping in {@link campaign-performance}.
 */
import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchStatsDataSetHistory,
  selectStatsDataSetHistory,
  selectStatsDataSetHistoryError,
  selectStatsDataSetHistoryLoading,
  type StatsHistoryEntry,
  type StatsHistoryRef,
} from '@/store/slices/ownerHubStats';

import type { Kpi, TopWebsite } from '../../mock-data/types';
import { liveBuckets, matchedBaseline, toBucketsFor } from '../home/stats-shape';
import { LIVE_REFRESH_MS, useRefreshTick } from '../home/useRefreshTick';
import { getAdCampaignPerformance, type AdPerformanceSite } from '../acquisition/api/casino-ad-service';
import {
  buildCampaignKpis,
  type CampaignColumn,
  computeCampaignWindows,
  type DateRange,
  formatMoney,
  mapTopSites,
} from './campaign-performance';

const DATA_SET = 'daily_affiliate_activity';
const DIMENSION = 'campaign';
const AGGREGATE_ON = [DIMENSION];

export type CampaignPerformanceResult = {
  /** Formatted lifetime Ad Spend for the hero (`''` while loading, `N/A` on failure). */
  heroAdSpend: string;
  /** The six performance KPIs with real figures (chrome only while loading). */
  kpis: Kpi[];
  /** Top publisher sites (empty until the lifetime spend resolves / on failure). */
  topWebsites: TopWebsite[];
  /** Per-section loading, handed to the template in place of its mock latency. */
  loading: { heroAdSpend: boolean; performance: boolean; topWebsites: boolean };
};

/* ── ad-service spend (lifetime + four comparison windows, fetched directly) ─────── */

type AdState = {
  loading: boolean;
  errored: boolean;
  lifetimeCost: number;
  topSites: AdPerformanceSite[];
  costToday: number | null;
  costYesterday: number | null;
  costMonth: number | null;
  costLastMonth: number | null;
};

const AD_IDLE: AdState = {
  loading: false,
  errored: false,
  lifetimeCost: 0,
  topSites: [],
  costToday: null,
  costYesterday: null,
  costMonth: null,
  costLastMonth: null,
};

/* ── stats history (one redux entry per window, campaign-filtered) ───────────────── */

type Hist = { entry: StatsHistoryEntry | undefined; loading: boolean; error: string | undefined };

/** The three selectors for one history ref, in one place (stable hook order). */
const useHistory = (ref: StatsHistoryRef | undefined): Hist => ({
  entry: useAppSelector((s) => selectStatsDataSetHistory(s, ref)),
  loading: useAppSelector((s) => selectStatsDataSetHistoryLoading(s, ref)),
  error: useAppSelector((s) => selectStatsDataSetHistoryError(s, ref)),
});

/** Skeleton only while there is no cached entry yet (stale-while-revalidate). */
const sectionLoading = (h: Hist): boolean => h.entry === undefined && (h.loading || h.error === undefined);
const sectionErrored = (h: Hist): boolean => h.entry === undefined && h.error !== undefined;

/**
 * @param campaignId The ad-service campaign id (also the Stats `campaign` filter value);
 *        `undefined` for a non-API (mock-pool) campaign keeps the hook idle.
 * @param tenantId Active brand tenant id (`displayBrand?.tenant_id`) for the Ad Service
 *        proxy; `undefined` keeps the ad figures idle until a brand resolves.
 * @param casinoInstanceId Active brand id (`displayBrand?.id`) for the stats calls.
 * @param startSeconds The campaign's `start_date` (epoch seconds) for the hero's
 *        lifetime Ad Spend window.
 * @param chromeKpis The static KPI chrome (titles / subtitles) whose figures are
 *        overridden with the real data.
 */
export const useCampaignPerformance = (
  campaignId: string | undefined,
  tenantId: string | undefined,
  casinoInstanceId: string | undefined,
  startSeconds: number | null | undefined,
  chromeKpis: Kpi[],
): CampaignPerformanceResult => {
  const dispatch = useAppDispatch();
  const tick = useRefreshTick(true, LIVE_REFRESH_MS);

  // Windows roll over at UTC midnight (the clock read is a hidden time dep; the figures
  // are bucketed by UTC day, see computeCampaignWindows) and the tick re-polls the live
  // ones in place.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const windows = React.useMemo(() => computeCampaignWindows(Date.now(), startSeconds), [startSeconds, tick]);
  const ready = Boolean(casinoInstanceId && campaignId);

  /* ----- ad-service: lifetime + 4 windows, in one batched request ----- */
  const [ad, setAd] = React.useState<AdState>(AD_IDLE);
  // Every distinct date that can shift the batched request, so a window roll (UTC
  // midnight / month boundary) re-fetches — including `lastMonth.to`, which moves daily
  // with the month-to-date window. The redundant `*.to` mirrors are omitted (they equal
  // `today.from`).
  const a = windows.ad;
  const adKey = [campaignId ?? '', tenantId ?? '', a.lifetime.from, a.today.from, a.yesterday.from, a.month.from, a.lastMonth.from, a.lastMonth.to].join('|');
  React.useEffect(() => {
    if (!campaignId || !tenantId) {
      setAd(AD_IDLE);
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setAd((prev) => ({ ...prev, loading: true }));

    const costOf = (range: DateRange): ReturnType<typeof getAdCampaignPerformance> =>
      getAdCampaignPerformance(campaignId, tenantId, { breakdown: 'summary', dateFrom: range.from, dateTo: range.to }, signal);

    Promise.all([
      costOf(windows.ad.lifetime),
      costOf(windows.ad.today),
      costOf(windows.ad.yesterday),
      costOf(windows.ad.month),
      costOf(windows.ad.lastMonth),
    ])
      .then(([life, today, yesterday, month, lastMonth]) => {
        if (signal.aborted) return;
        // `cost` is in the response `currency` (e.g. USD), which we intentionally do NOT
        // convert: like the acquisition ad-spend KPIs (`buildAdSpendStat` / `formatValue`)
        // every money figure renders as `$`, assuming a single-currency brand. Convert
        // here + in the stats money fields if a brand's ad-spend and reporting currencies
        // ever differ (no multi-currency data to confirm against yet — see missing-data.md).
        setAd({
          loading: false,
          errored: false,
          lifetimeCost: life.totals.cost ?? 0,
          topSites: life.top_sites ?? [],
          costToday: today.totals.cost ?? 0,
          costYesterday: yesterday.totals.cost ?? 0,
          costMonth: month.totals.cost ?? 0,
          costLastMonth: lastMonth.totals.cost ?? 0,
        });
      })
      .catch(() => {
        if (!signal.aborted) setAd({ ...AD_IDLE, errored: true });
      });

    return () => controller.abort();
    // adKey + tick capture every window change (midnight roll) and the live re-poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adKey, tick]);

  /* ----- stats history: today / yesterday / month / last-month, campaign-filtered ----- */
  const histRef = (range: DateRange): StatsHistoryRef | undefined =>
    ready
      ? {
          casinoInstanceId: casinoInstanceId as string,
          dataSet: DATA_SET,
          startDate: range.from,
          endDate: range.to,
          intervalSlice: '1 day',
          aggregateOn: AGGREGATE_ON,
          filter: { [DIMENSION]: campaignId as string },
        }
      : undefined;

  const today = useHistory(histRef(windows.stats.today));
  const yesterday = useHistory(histRef(windows.stats.yesterday));
  const month = useHistory(histRef(windows.stats.month));
  const lastMonth = useHistory(histRef(windows.stats.lastMonth));

  const dispatchHistory = React.useCallback(
    (from: string, to: string): void => {
      dispatch(
        fetchStatsDataSetHistory({
          casinoInstanceId: casinoInstanceId as string,
          dataSet: DATA_SET,
          startDate: from,
          endDate: to,
          intervalSlice: '1 day',
          aggregateOn: [...AGGREGATE_ON],
          filter: { [DIMENSION]: campaignId as string },
        }),
      );
    },
    [dispatch, casinoInstanceId, campaignId],
  );

  const { today: todayW, yesterday: yesterdayW, month: monthW, lastMonth: lastMonthW } = windows.stats;

  // Live windows (today + this month include today) re-dispatch on the tick.
  React.useEffect(() => {
    if (!ready) return;
    dispatchHistory(todayW.from, todayW.to);
    dispatchHistory(monthW.from, monthW.to);
  }, [ready, dispatchHistory, todayW.from, todayW.to, monthW.from, monthW.to, tick]);

  // Final windows (yesterday + last month never include today) fetch once per window.
  React.useEffect(() => {
    if (!ready) return;
    dispatchHistory(yesterdayW.from, yesterdayW.to);
    dispatchHistory(lastMonthW.from, lastMonthW.to);
  }, [ready, dispatchHistory, yesterdayW.from, yesterdayW.to, lastMonthW.from, lastMonthW.to]);

  // A non-API (mock-pool) campaign has no real source — stay idle; the dispatcher keeps
  // that legacy sample on its mock figures rather than reading these.
  if (!campaignId) {
    return {
      heroAdSpend: '',
      kpis: chromeKpis,
      topWebsites: [],
      loading: { heroAdSpend: false, performance: false, topWebsites: false },
    };
  }

  const now = Date.now();
  const buckets = (h: Hist, live: boolean): ReturnType<typeof toBucketsFor> => {
    const raw = toBucketsFor(h.entry?.data, DIMENSION, campaignId);
    return live ? liveBuckets(raw, now) : raw;
  };

  const todayBuckets = buckets(today, true);
  const monthBuckets = buckets(month, true);
  // With no brand resolved the stats can't be queried, so the conversion figures read
  // `N/A` (not a stuck skeleton) while the ad-sourced Ad Spend / CPA still resolve.
  const left: CampaignColumn = {
    current: todayBuckets,
    baseline: matchedBaseline(todayBuckets, buckets(yesterday, false)),
    statsErrored: !ready || sectionErrored(today),
    cost: ad.costToday,
    baselineCost: ad.costYesterday,
    costErrored: ad.errored,
  };
  const right: CampaignColumn = {
    current: monthBuckets,
    baseline: matchedBaseline(monthBuckets, buckets(lastMonth, false)),
    statsErrored: !ready || sectionErrored(month),
    cost: ad.costMonth,
    baselineCost: ad.costLastMonth,
    costErrored: ad.errored,
  };

  // The stats can only be loading once a brand is resolved; otherwise they resolve to
  // `N/A` above rather than gating the grid forever.
  const statsLoading = ready && (sectionLoading(today) || sectionLoading(month));
  const performanceLoading = ad.loading || statsLoading;

  return {
    heroAdSpend: ad.loading ? '' : ad.errored ? 'N/A' : formatMoney(ad.lifetimeCost),
    kpis: performanceLoading ? chromeKpis : buildCampaignKpis(chromeKpis, left, right),
    topWebsites: ad.loading || ad.errored ? [] : mapTopSites(ad.topSites),
    loading: { heroAdSpend: ad.loading, performance: performanceLoading, topWebsites: ad.loading },
  };
};

export default useCampaignPerformance;
